// src/pages/nascar/XfCheatSheets.jsx
import React, { useEffect, useMemo, useState } from "react";

/* ------------------------------ constants ------------------------------ */

const BASE = (import.meta?.env?.BASE_URL || "/").replace(/\/+$/, "/");

const PROJECTIONS_URL = `${BASE}data/nascar/xfinity/latest/projections.json`;
const ODDS_URL = `${BASE}data/nascar/xfinity/latest/odds_main.json`;
const DRIVER_DATA_URL = `${BASE}data/nascar/xfinity/latest/data.json`;
const META_URL = `${BASE}data/nascar/xfinity/latest/meta.json`;

// e.g. Car "5" -> public/logos/nascar/Xfinity/5.png
const carLogo = (car) => {
  if (!car) return "";

  const clean = String(car)
    .trim()
    .replace(/^#/, "")
    .replace(/^no\.?\s*/i, "");

  return `${BASE}logos/nascar/Xfinity/${clean}.png`;
};

/* --------------------------- data fetchers ----------------------------- */

function useSourceData(projectionsUrl, oddsUrl) {
  const [projections, setProjections] = useState([]);
  const [odds, setOdds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const [projRes, oddsRes] = await Promise.all([
          fetch(`${projectionsUrl}?_=${Date.now()}`, { cache: "no-store" }),
          fetch(`${oddsUrl}?_=${Date.now()}`, { cache: "no-store" }),
        ]);

        if (!projRes.ok) {
          throw new Error(`Projections HTTP ${projRes.status}`);
        }

        if (!oddsRes.ok) {
          throw new Error(`Odds HTTP ${oddsRes.status}`);
        }

        const [projJson, oddsJson] = await Promise.all([
          projRes.json(),
          oddsRes.json(),
        ]);

        const projRows = Array.isArray(projJson)
          ? projJson
          : Array.isArray(projJson?.rows)
          ? projJson.rows
          : Array.isArray(projJson?.data)
          ? projJson.data
          : [];

        const oddsRows = Array.isArray(oddsJson)
          ? oddsJson
          : Array.isArray(oddsJson?.rows)
          ? oddsJson.rows
          : Array.isArray(oddsJson?.data)
          ? oddsJson.data
          : [];

        if (alive) {
          setProjections(projRows);
          setOdds(oddsRows);
        }
      } catch (e) {
        if (alive) {
          setProjections([]);
          setOdds([]);
          setErr(String(e));
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [projectionsUrl, oddsUrl]);

  return { projections, odds, loading, err };
}

// Fetch driver-level data.json and build a map: normalizedName -> row
function useDriverData(url) {
  const [driverMap, setDriverMap] = useState(new Map());

  useEffect(() => {
    let alive = true;
    if (!url) return;

    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();

        const j = await r.json();
        const map = new Map();

        if (Array.isArray(j)) {
          j.forEach((row) => {
            if (!row || typeof row !== "object") return;

            const name =
              row.Driver ||
              row.driver ||
              row.Name ||
              row.name;

            if (!name) return;
            map.set(normalizeName(name), row);
          });
        } else if (Array.isArray(j?.rows)) {
          j.rows.forEach((row) => {
            const name =
              row.Driver ||
              row.driver ||
              row.Name ||
              row.name;

            if (!name) return;
            map.set(normalizeName(name), row);
          });
        }

        if (alive) setDriverMap(map);
      } catch {
        if (alive) setDriverMap(new Map());
      }
    })();

    return () => {
      alive = false;
    };
  }, [url]);

  return driverMap;
}

function useMeta(url) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!url) return;

    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();

        const j = await r.json();
        if (alive) setMeta(j);
      } catch {
        if (alive) setMeta(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [url]);

  return meta;
}

/* ------------------------------- helpers ------------------------------- */

function cheatSheetSortKey(label = "") {
  const l = label.toLowerCase();

  // betting / odds tables → push to bottom
  if (
    l.includes("win%") ||
    l.includes("t3") ||
    l.includes("t5") ||
    l.includes("t10")
  ) {
    return 2;
  }

  // DK / FD fantasy tables
  return 1;
}

function normalizeName(name) {
  return String(name || "").trim().toLowerCase();
}

function numberValue(v) {
  if (
    v === null ||
    v === undefined ||
    String(v).trim() === ""
  ) {
    return null;
  }

  const raw = String(v)
    .trim()
    .replace(/,/g, "")
    .replace(/%/g, "")
    .replace(/\$/g, "");

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// In these NASCAR feeds, an unpopulated numeric metric can arrive as 0.
// For Top-10 rankings, treat that placeholder 0 as blank.
function hasMetricValue(v) {
  const n = numberValue(v);
  return n !== null && n !== 0;
}

function getField(row, keys) {
  for (const key of keys) {
    if (
      row &&
      Object.prototype.hasOwnProperty.call(row, key)
    ) {
      return row[key];
    }
  }

  return "";
}

// Converts placeholder 0 values to blank for display.
function blankZero(v) {
  const n = numberValue(v);
  if (n === 0) return "";
  return v ?? "";
}

function top10(rows, metricKeys) {
  return [...(rows || [])]
    .filter((row) => {
      const v = getField(row, metricKeys);
      return hasMetricValue(v);
    })
    .sort((a, b) => {
      const av = numberValue(getField(a, metricKeys));
      const bv = numberValue(getField(b, metricKeys));
      return (bv ?? -Infinity) - (av ?? -Infinity);
    })
    .slice(0, 10);
}

/*
 * Fantasy cheat sheet shape:
 *   Top 10 ...
 *   Qual
 *   Salary
 *   Metric
 */
function makeFantasyTable({
  id,
  label,
  rows,
  metricKeys,
  metricLabel,
  salaryKeys,
  salaryLabel,
}) {
  const ranked = top10(rows, metricKeys);

  return {
    id,
    label,
    rows: ranked.map((row) => ({
      [label]: row.Driver,
      Qual: blankZero(
        getField(row, ["Qual", "QUAL", "Start", "Start Pos"])
      ),
      [salaryLabel]: getField(row, salaryKeys),
      [metricLabel]: blankZero(getField(row, metricKeys)),
    })),
  };
}

/*
 * Betting cheat sheet shape:
 *   Top 10 ...
 *   Proj Fin
 *   Metric
 */
function makeBettingTable({
  id,
  label,
  rows,
  metricKeys,
  metricLabel,
}) {
  const ranked = top10(rows, metricKeys);

  return {
    id,
    label,
    rows: ranked.map((row) => ({
      [label]: row.Driver,
      "Proj Fin": blankZero(
        getField(row, ["Proj Fin", "Proj Finish", "Projected Finish"])
      ),
      [metricLabel]: blankZero(getField(row, metricKeys)),
    })),
  };
}

/*
 * Rebuild the same Top-10 cheat-sheet tables directly from
 * projections.json + odds_main.json.
 *
 * Betting leverage tables are intentionally removed:
 * - Top 10 Win Leverage%
 * - Top 10 T3 Leverage%
 * - Top 10 T5 Leverage%
 * - Top 10 T10 Leverage%
 */
function buildCheatSheetTables(projections, odds) {
  return [
    makeFantasyTable({
      id: "top_10_dk_projections",
      label: "Top 10 DK Projections",
      rows: projections,
      metricKeys: ["DK Proj"],
      metricLabel: "DK Proj",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_projections",
      label: "Top 10 FD Projections",
      rows: projections,
      metricKeys: ["FD Proj"],
      metricLabel: "FD Proj",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_value",
      label: "Top 10 DK Value",
      rows: projections,
      metricKeys: ["DK Val", "DK Value"],
      metricLabel: "DK Val",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_value",
      label: "Top 10 FD Value",
      rows: projections,
      metricKeys: ["FD Val", "FD Value"],
      metricLabel: "FD Val",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_floor",
      label: "Top 10 DK Floor",
      rows: projections,
      metricKeys: ["DK Floor"],
      metricLabel: "DK Floor",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_floor",
      label: "Top 10 FD Floor",
      rows: projections,
      metricKeys: ["FD Floor"],
      metricLabel: "FD Floor",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_ceiling",
      label: "Top 10 DK Ceiling",
      rows: projections,
      metricKeys: ["DK Ceiling"],
      metricLabel: "DK Ceiling",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_ceiling",
      label: "Top 10 FD Ceiling",
      rows: projections,
      metricKeys: ["FD Ceiling"],
      metricLabel: "FD Ceiling",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_pown",
      label: "Top 10 DK pOwn%",
      rows: projections,
      metricKeys: ["DK pOWN%", "DK pOwn%", "DK pOWN", "DK pOwn"],
      metricLabel: "DK pOWN%",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_pown",
      label: "Top 10 FD pOwn%",
      rows: projections,
      metricKeys: ["FD pOWN%", "FD pOwn%", "FD pOWN", "FD pOwn"],
      metricLabel: "FD pOWN%",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_opt",
      label: "Top 10 DK Opt%",
      rows: projections,
      metricKeys: ["DK Opt%", "DK Opt"],
      metricLabel: "DK Opt%",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_opt",
      label: "Top 10 FD Opt%",
      rows: projections,
      metricKeys: ["FD Opt%", "FD Opt"],
      metricLabel: "FD Opt%",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_leverage",
      label: "Top 10 DK Leverage",
      rows: projections,
      metricKeys: ["DK Lev%", "DK Lev", "DK Leverage", "DK Leverage%"],
      metricLabel: "DK Lev%",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_leverage",
      label: "Top 10 FD Leverage",
      rows: projections,
      metricKeys: ["FD Lev%", "FD Lev", "FD Leverage", "FD Leverage%"],
      metricLabel: "FD Lev%",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeFantasyTable({
      id: "top_10_dk_rating",
      label: "Top 10 DK Rating",
      rows: projections,
      metricKeys: ["DK Rtg", "DK RTG", "DK Rating"],
      metricLabel: "DK Rtg",
      salaryKeys: ["DK Sal", "DK Salary"],
      salaryLabel: "DK Sal",
    }),

    makeFantasyTable({
      id: "top_10_fd_rating",
      label: "Top 10 FD Rating",
      rows: projections,
      metricKeys: ["FD Rtg", "FD RTG", "FD Rating"],
      metricLabel: "FD RTG",
      salaryKeys: ["FD Sal", "FD Salary"],
      salaryLabel: "FD Sal",
    }),

    makeBettingTable({
      id: "top_10_win",
      label: "Top 10 Win%",
      rows: odds,
      metricKeys: ["Win%", "WIN%", "Win %"],
      metricLabel: "Win%",
    }),

    makeBettingTable({
      id: "top_10_t3",
      label: "Top 10 T3%",
      rows: odds,
      metricKeys: ["T3%", "Top 3%", "T3 %"],
      metricLabel: "T3%",
    }),

    makeBettingTable({
      id: "top_10_t5",
      label: "Top 10 T5%",
      rows: odds,
      metricKeys: ["T5%", "Top 5%", "T5 %"],
      metricLabel: "T5%",
    }),

    makeBettingTable({
      id: "top_10_t10",
      label: "Top 10 T10%",
      rows: odds,
      metricKeys: ["T10%", "Top 10%", "T10 %"],
      metricLabel: "T10%",
    }),
  ];
}

const fmt = {
  smart(v) {
    if (
      v === null ||
      v === undefined ||
      String(v).trim() === ""
    ) {
      return "";
    }

    const raw = String(v).trim();

    // Preserve percentage strings exactly as supplied.
    if (raw.includes("%")) {
      return raw;
    }

    const n = Number(raw.replace(/[, ]/g, ""));

    if (!Number.isFinite(n)) {
      return raw;
    }

    return Number.isInteger(n)
      ? String(n)
      : String(n.toFixed(1)).replace(/\.0$/, "");
  },

  salary(v) {
    if (
      v === null ||
      v === undefined ||
      String(v).trim() === ""
    ) {
      return "";
    }

    const n = Number(String(v).replace(/[$, ]/g, ""));

    if (!Number.isFinite(n)) {
      return String(v);
    }

    const k = n / 1000;

    return Number.isInteger(k)
      ? `${k}k`
      : `${k.toFixed(1).replace(/\.0$/, "")}k`;
  },
};

function fmtUpdated(meta) {
  const raw =
    meta?.updated ||
    meta?.last_updated ||
    meta?.lastUpdated ||
    meta?.timestamp;

  if (!raw) return null;

  const d = new Date(raw);

  if (isNaN(d.getTime())) return null;

  return d.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Heuristic: which column should get the car logo (if table has a car column)
function isCarColumn(name) {
  const s = String(name || "").toLowerCase().trim();

  if (!s) return false;

  if (
    s === "car" ||
    s === "car#" ||
    s === "#" ||
    s === "no" ||
    s === "number" ||
    s === "car_num" ||
    s === "car number"
  ) {
    return true;
  }

  if (
    s.includes("car") &&
    (s.includes("#") || s.includes("no") || s.includes("num"))
  ) {
    return true;
  }

  return false;
}

// The first column of each generated table is the driver-name column.
function isDriverColumn(name) {
  const s = String(name || "").toLowerCase().trim();

  return (
    s === "driver" ||
    s === "name" ||
    s === "driver name" ||
    s.startsWith("top 10 ")
  );
}

// Pull car number out of a driver row from data.json
function getCarFromDriverRow(row) {
  if (!row || typeof row !== "object") return null;

  for (const key of Object.keys(row)) {
    if (isCarColumn(key)) {
      return row[key];
    }
  }

  return null;
}

/* -------------------------------- page -------------------------------- */

export default function XfCheatSheets() {
  const {
    projections,
    odds,
    loading,
    err,
  } = useSourceData(PROJECTIONS_URL, ODDS_URL);

  const driverMap = useDriverData(DRIVER_DATA_URL);
  const meta = useMeta(META_URL);

  const tables = useMemo(
    () => buildCheatSheetTables(projections, odds),
    [projections, odds]
  );

  const [selId, setSelId] = useState("");
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";

  const options = useMemo(() => {
    return tables
      .map((t) => ({ id: t.id, label: t.label }))
      .sort((a, b) => {
        const ka = cheatSheetSortKey(a.label);
        const kb = cheatSheetSortKey(b.label);

        if (ka !== kb) return ka - kb;
        return a.label.localeCompare(b.label);
      });
  }, [tables]);

  useEffect(() => {
    if (!selId && options.length) {
      setSelId(options[0].id);
    }
  }, [options, selId]);

  const table = useMemo(
    () => tables.find((t) => t.id === selId) || null,
    [tables, selId]
  );

  const columns = useMemo(() => {
    if (!table?.rows?.length) return [];
    return Object.keys(table.rows[0]);
  }, [table]);

  const driverColName = useMemo(
    () => columns.find((c) => isDriverColumn(c)) || null,
    [columns]
  );

  const updatedStamp = fmtUpdated(meta);
  const rowCount = table?.rows?.length || 0;

  // Active series for header switch (this page is Xfinity-specific)
  const activeSeries = "xfinity";

  /* ---------------------------- shared styling --------------------------- */

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800/90 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-950/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "rounded-2xl border border-slate-200 bg-white shadow-lg";

  const headerBgClass = isDark
    ? "bg-slate-900/95 text-slate-100"
    : "bg-slate-50 text-slate-800";

  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900/85" : "bg-slate-50/80";

  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";

  const cell = "px-2.5 py-1 text-left";
  const headerBase = "px-2.5 py-1.5 text-left font-semibold";
  const small = "text-[11.5px] md:text-[12.5px]";

  return (
    <div className={outerClass}>
      <div className="mx-auto max-w-3xl px-3 md:px-4 py-6 md:py-8">

        {/* Top header: title + meta + theme toggle */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              NASCAR O'Reilly — Cheat Sheets
            </h1>

            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs sm:text-[13px] text-slate-400">
              {!loading && !err && rowCount ? (
                <span>{rowCount.toLocaleString()} rows</span>
              ) : null}

              {updatedStamp && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-800/70 px-2.5 py-1 text-[11px] sm:text-[12px] text-slate-200">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Updated: {updatedStamp}
                </span>
              )}

              {/* Series switch (Cup / O'Reilly / Trucks) */}
              <div className="flex items-center gap-3">
                {[
                  ["cup", "Cup"],
                  ["xfinity", "O'Reilly"],
                  ["trucks", "Trucks"],
                ].map(([slug, label]) => (
                  <a
                    key={slug}
                    href={`/nascar/${slug === "xfinity" ? "oreilly" : slug}/cheatsheets`}
                    className={[
                      "font-semibold transition-colors",
                      "text-sky-400 hover:text-sky-300",
                      slug === activeSeries ? "underline decoration-2" : "",
                    ].join(" ")}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Theme toggle */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-[11px] sm:text-xs text-slate-400">
              Theme
            </span>

            <div className="inline-flex rounded-full bg-slate-900/70 p-1 border border-slate-700/70">
              {["dark", "light"].map((t) => {
                const active = theme === t;

                return (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={
                      "px-2.5 py-1 rounded-full text-[11px] sm:text-xs transition " +
                      (active
                        ? "bg-sky-500 text-slate-950 shadow"
                        : "text-slate-300 hover:text-white")
                    }
                  >
                    {t === "dark" ? "Dark" : "Light"}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selector + card */}
        <div className="space-y-3">
          {/* table selector */}
          <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
            <label className="font-medium text-slate-400">
              Cheat sheet table
            </label>

            <select
              className={
                "h-8 rounded-full border px-3 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100"
                  : "bg-white border-slate-300 text-slate-900")
              }
              value={selId}
              onChange={(e) => setSelId(e.target.value)}
            >
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* main card */}
          <div className={cardClass}>
            {loading ? (
              <div className="px-6 py-10 text-center text-slate-400 text-sm">
                Loading…
              </div>
            ) : err ? (
              <div className="px-6 py-10 text-center text-red-400 text-sm">
                Error: {err}
              </div>
            ) : !table ? (
              <div className="px-6 py-10 text-center text-slate-400 text-sm">
                No data.
              </div>
            ) : table.rows.length === 0 ? (
              <div className="px-6 py-10 text-center text-slate-400 text-sm">
                No data available for this category yet.
              </div>
            ) : (
              <>
                {/* Single table header only — no duplicate title bar */}
                <div className="overflow-auto h-[70vh] rounded-2xl">
                  <table
                    className={`min-w-max w-full border-separate ${small} ${tableTextClass}`}
                    style={{ borderSpacing: 0 }}
                  >
                    <thead className={headerBgClass + " sticky top-0 z-20"}>
                      <tr>
                        {columns.map((col, idx) => (
                          <th
                            key={idx}
                            className={headerBase + " whitespace-nowrap"}
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {table.rows.map((row, ri) => {
                        const rowBg =
                          ri % 2 === 0 ? rowOddClass : rowEvenClass;

                        return (
                          <tr key={ri} className={rowBg}>
                            {columns.map((col, ci) => {
                              const val = row?.[col];
                              const isDriverCol =
                                driverColName && col === driverColName;
                              const showCarLogo =
                                !isDriverCol && isCarColumn(col);

                              // Driver column: look up car in data.json by driver name
                              if (isDriverCol) {
                                const driverName = val;

                                const driverRow =
                                  driverMap.get(normalizeName(driverName)) ||
                                  null;

                                const carVal =
                                  getCarFromDriverRow(driverRow);

                                return (
                                  <td
                                    key={ci}
                                    className={
                                      cell +
                                      " whitespace-nowrap border-t border-slate-800/40"
                                    }
                                  >
                                    <div className="flex items-center gap-2">
                                      {carVal ? (
                                        <img
                                          src={carLogo(carVal)}
                                          alt=""
                                          className="w-5 h-5 object-contain opacity-90"
                                          onError={(e) => {
                                            e.currentTarget.style.display =
                                              "none";
                                          }}
                                        />
                                      ) : null}

                                      <span>{driverName}</span>
                                    </div>
                                  </td>
                                );
                              }

                              // Explicit car column, if one is ever added
                              if (showCarLogo) {
                                return (
                                  <td
                                    key={ci}
                                    className={
                                      cell +
                                      " whitespace-nowrap border-t border-slate-800/40"
                                    }
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <img
                                        src={carLogo(val)}
                                        alt=""
                                        className="w-5 h-5 object-contain opacity-90"
                                        onError={(e) => {
                                          e.currentTarget.style.display =
                                            "none";
                                        }}
                                      />

                                      <span>{fmt.smart(val)}</span>
                                    </div>
                                  </td>
                                );
                              }

                              // Normal cell
                              return (
                                <td
                                  key={ci}
                                  className={
                                    cell +
                                    " whitespace-nowrap border-t border-slate-800/40"
                                  }
                                >
                                  {col === "DK Sal" || col === "FD Sal"
                                    ? fmt.salary(val)
                                    : fmt.smart(val)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
